import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import {
  documentCategories,
  featuredDocumentCollections
} from '../data/corporateContent';
import { ROUTE_PATHS } from '../config/navigation';

const DocumentsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPath, setCurrentPath] = useState('/');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const { toasts, showToast, removeToast } = useToast();

  const createSampleDocuments = useCallback(async () => {
    const sampleDocuments = [
      {
        name: 'İnsan Kaynakları Politikaları',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Kalite Yönetim Sistemleri',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Proje Şablonları',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'forms'
      },
      {
        name: 'İş Sağlığı ve Güvenliği',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Yıllık İzin Prosedürü.pdf',
        type: 'pdf',
        size: '1.2 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'ISO_9001_Sertifikası.pdf',
        type: 'pdf',
        size: '850 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'Çalışan_El_Kitabı.docx',
        type: 'word',
        size: '2.5 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-doc',
        color: 'text-primary',
        category: 'corporate'
      },
      {
        name: 'Organizasyon_Şeması.xlsx',
        type: 'excel',
        size: '890 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-spreadsheet',
        color: 'text-success',
        category: 'corporate'
      },
      {
        name: 'Şirket_Sunumu.pptx',
        type: 'powerpoint',
        size: '15.3 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-export',
        color: 'text-orange',
        category: 'corporate'
      },
      {
        name: 'Güvenlik_Politikası.pdf',
        type: 'pdf',
        size: '675 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'Masraf_Bildirim_Formu.xlsx',
        type: 'excel',
        size: '420 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-spreadsheet',
        color: 'text-success',
        category: 'forms'
      },
      {
        name: 'Dilekçe_Örneği.docx',
        type: 'word',
        size: '310 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-doc',
        color: 'text-primary',
        category: 'forms'
      }
    ];

    try {
      for (const doc of sampleDocuments) {
        await api.createDocument(doc);
      }

      setDocuments(sampleDocuments);
    } catch (error) {
      console.error('Örnek dokümanlar oluşturulurken hata:', error);
      setDocuments(sampleDocuments);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Dokümanlar yüklenirken hata:', error);
      await createSampleDocuments();
    } finally {
      setLoading(false);
    }
  }, [createSampleDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const requestedCategory = searchParams.get('kategori');
    const matchedCategory = documentCategories.find(
      (category) => category.queryValue === requestedCategory
    );

    setSelectedCategory(matchedCategory?.id || 'all');
  }, [searchParams]);

  const inferCategory = useCallback((document) => {
    if (document.category) {
      return document.category;
    }

    const searchableText = `${document.name || ''} ${document.description || ''}`.toLocaleLowerCase('tr-TR');
    const matchedCategory = documentCategories.find(
      (category) =>
        category.id !== 'all' &&
        category.keywords.some((keyword) => searchableText.includes(keyword.toLocaleLowerCase('tr-TR')))
    );

    return matchedCategory?.id || 'all';
  }, []);

  const documentsWithCategory = useMemo(
    () => documents.map((document) => ({ ...document, resolvedCategory: inferCategory(document) })),
    [documents, inferCategory]
  );

  const filteredDocuments = useMemo(
    () =>
      documentsWithCategory.filter((doc) => {
        const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || doc.resolvedCategory === selectedCategory;
        return matchesSearch && matchesCategory;
      }),
    [documentsWithCategory, searchTerm, selectedCategory]
  );

  const categoryCounts = useMemo(
    () =>
      documentCategories.reduce((accumulator, category) => {
        accumulator[category.id] =
          category.id === 'all'
            ? documentsWithCategory.length
            : documentsWithCategory.filter((doc) => doc.resolvedCategory === category.id).length;
        return accumulator;
      }, {}),
    [documentsWithCategory]
  );

  const setCategoryFromCard = (categoryId) => {
    setSelectedCategory(categoryId);
    const category = documentCategories.find((item) => item.id === categoryId);

    if (!category || category.id === 'all') {
      setSearchParams({});
      return;
    }

    setSearchParams({ kategori: category.queryValue });
  };

  const formatFileSize = (size) => {
    if (!size) return '-';
    return size;
  };

  const getFileTypeLabel = (type) => {
    const types = {
      folder: 'Klasör',
      pdf: 'PDF Belgesi',
      word: 'Word Belgesi',
      excel: 'Excel Belgesi',
      powerpoint: 'PowerPoint Sunumu',
      image: 'Resim',
      other: 'Diğer'
    };

    return types[type] || 'Bilinmeyen';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: 'bx-file-pdf',
      doc: 'bx-file-doc',
      docx: 'bx-file-doc',
      xls: 'bx-file',
      xlsx: 'bx-file',
      ppt: 'bx-file',
      pptx: 'bx-file',
      jpg: 'bx-image',
      jpeg: 'bx-image',
      png: 'bx-image',
      gif: 'bx-image',
      txt: 'bx-file-txt',
      zip: 'bx-archive',
      rar: 'bx-archive'
    };

    return iconMap[ext] || 'bx-file';
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadForm((prev) => ({ ...prev, file }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.file) {
      showToast('Lütfen bir dosya seçin', 'error');
      return;
    }

    if (!uploadForm.title.trim()) {
      showToast('Lütfen dosya başlığı girin', 'error');
      return;
    }

    if (uploadForm.file.size > 50 * 1024 * 1024) {
      showToast("Dosya boyutu 50 MB'dan büyük olamaz", 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }

          return prev + 10;
        });
      }, 100);

      const response = await api.uploadDocument(
        uploadForm.file,
        uploadForm.title,
        uploadForm.description
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      setDocuments((prev) => [response.data, ...prev]);
      setUploadForm({ title: '', description: '', file: null });
      setShowUploadModal(false);

      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        showToast('Dosya başarıyla yüklendi!', 'success');
      }, 500);
    } catch (error) {
      console.error('Dosya yüklenirken hata:', error);
      showToast(`Dosya yüklenirken hata oluştu: ${error.message}`, 'error');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (documentId) => {
    const selectedDocument = documents.find((item) => (item._id || item.id) === documentId);

    if (selectedDocument?.type === 'folder') {
      showToast('Klasörler indirilemez. Lütfen içindeki dosyaları seçin.', 'info');
      return;
    }

    try {
      const { blob, filename } = await api.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename || selectedDocument?.originalName || selectedDocument?.name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('Dosya indirme işlemi başlatıldı.', 'success');
    } catch (error) {
      console.error('Dosya indirilirken hata:', error);
      showToast(`Dosya indirilirken hata oluştu: ${error.message}`, 'error');
    }
  };

  const handleDelete = async (documentId, documentName) => {
    showToast(`"${documentName}" dosyasını silmek istediğinizden emin misiniz?`, 'warning', {
      duration: 0,
      actions: [
        {
          label: 'Evet, Sil',
          onClick: async () => {
            try {
              await api.deleteDocument(documentId);
              setDocuments((prev) => prev.filter((doc) => (doc._id || doc.id) !== documentId));
              showToast('Dosya başarıyla silindi!', 'success');
            } catch (error) {
              console.error('Dosya silinirken hata:', error);
              showToast(`Dosya silinirken hata oluştu: ${error.message}`, 'error');
            }
          }
        },
        {
          label: 'İptal',
          onClick: () => {}
        }
      ]
    });
  };

  if (loading) {
    return (
      <div className="modern-loading">
        <div className="text-center">
          <div className="modern-spinner spinner-border mb-3" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="text-muted">Dokümanlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4 corporate-documents-hero">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h3 className="fw-bold text-dark mb-1">Doküman Merkezi</h3>
                <p className="text-muted mb-0">
                  Politika, prosedür, form, şablon ve kurumsal kaynaklar için merkezi erişim alanı
                </p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="badge bg-primary rounded-pill px-3 py-2">
                  <i className="bx bx-folder me-1"></i>
                  {filteredDocuments.length} Doküman
                </div>
                <button className="btn btn-modern-secondary" onClick={() => navigate(ROUTE_PATHS.announcements)}>
                  Duyurular
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        {featuredDocumentCollections.map((collection) => (
          <div key={collection.id} className="col-lg-4 col-md-6 mb-3">
            <button
              className="modern-card corporate-category-card w-100 text-start"
              onClick={() => setCategoryFromCard(collection.categoryId)}
            >
              <div className="corporate-category-card-header">
                <h5>{collection.title}</h5>
                <span>{categoryCounts[collection.categoryId] || 0}</span>
              </div>
              <p>{collection.description}</p>
              <strong>{collection.ctaLabel}</strong>
            </button>
          </div>
        ))}
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4">
            <div className="corporate-category-filters mb-4">
              {documentCategories.map((category) => (
                <button
                  key={category.id}
                  className={`corporate-filter-chip ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setCategoryFromCard(category.id)}
                >
                  <i className={category.icon}></i>
                  <span>{category.shortLabel}</span>
                  <small>{categoryCounts[category.id] || 0}</small>
                </button>
              ))}
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb modern-breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <button
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => setCurrentPath('/')}
                      style={{ color: '#2563eb' }}
                    >
                      <i className="bx bx-home me-1"></i> Ana Dizin
                    </button>
                  </li>
                  {currentPath !== '/' && <li className="breadcrumb-item active">Klasör</li>}
                </ol>
              </nav>

              <div className="d-flex align-items-center gap-3">
                <div className="input-group" style={{ width: '350px' }}>
                  <span className="input-group-text modern-search-icon">
                    <i className="bx bx-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder="Dokümanlarda ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <button className="btn btn-modern-secondary px-3">
                  <i className="bx bx-category-alt me-1"></i>
                  {documentCategories.find((item) => item.id === selectedCategory)?.title || 'Tüm Kaynaklar'}
                </button>

                <button
                  className="btn btn-modern-primary px-4"
                  onClick={() => setShowUploadModal(true)}
                  disabled={uploading}
                >
                  <i className="bx bx-upload me-1"></i>
                  {uploading ? 'Yükleniyor...' : 'Dosya Yükle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploading && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="modern-card p-4">
              <div className="d-flex align-items-center">
                <div className="modern-spinner spinner-border-sm me-3" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-medium">Dosya yükleniyor...</span>
                    <span className="text-muted">{uploadProgress}%</span>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <div className="modern-card">
            <div className="p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 modern-table">
                  <thead>
                    <tr>
                      <th className="modern-table-header">Dosya Adı</th>
                      <th className="modern-table-header">Tür</th>
                      <th className="modern-table-header">Kategori</th>
                      <th className="modern-table-header">Boyut</th>
                      <th className="modern-table-header">Değiştirilme</th>
                      <th className="modern-table-header text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map((item) => (
                      <tr key={item._id || item.id} className="modern-table-row">
                        <td className="modern-table-cell">
                          <div className="d-flex align-items-center">
                            <div className="modern-file-icon me-3">
                              <i className={`${item.icon} ${item.color}`}></i>
                            </div>
                            <div>
                              <div className="fw-medium text-dark">{item.name}</div>
                              {item.description && (
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                  {item.description}
                                </div>
                              )}
                              <small className="text-muted">{getFileTypeLabel(item.type)}</small>
                            </div>
                          </div>
                        </td>
                        <td className="modern-table-cell">
                          <span className="modern-badge modern-badge-secondary">
                            {getFileTypeLabel(item.type)}
                          </span>
                        </td>
                        <td className="modern-table-cell">
                          <span className="modern-badge modern-badge-secondary">
                            {documentCategories.find((category) => category.id === item.resolvedCategory)?.shortLabel || 'Tümü'}
                          </span>
                        </td>
                        <td className="modern-table-cell text-muted">{formatFileSize(item.size)}</td>
                        <td className="modern-table-cell text-muted">
                          {new Date(item.modified).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="modern-table-cell text-center">
                          <div className="modern-action-buttons">
                            <button
                              className="btn btn-sm modern-btn-icon"
                              title="İndir"
                              onClick={() => handleDownload(item._id || item.id)}
                            >
                              <i className="bx bx-download"></i>
                            </button>
                            <button
                              className="btn btn-sm modern-btn-icon text-danger"
                              title="Sil"
                              onClick={() => handleDelete(item._id || item.id, item.name)}
                              style={{ color: '#dc3545' }}
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <div className="modern-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modern-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modern-upload-header">
              <div className="d-flex align-items-center">
                <div className="modern-upload-icon">
                  <i className="bx bx-cloud-upload"></i>
                </div>
                <div>
                  <h4 className="modern-upload-title mb-1">Dosya Yükle</h4>
                  <p className="modern-upload-subtitle">Yeni dosyanızı sisteme ekleyin</p>
                </div>
              </div>
              <button className="modern-upload-close" onClick={() => setShowUploadModal(false)}>
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="modern-upload-body">
              <div className="modern-drop-zone">
                <div className="modern-drop-content">
                  <div className="modern-drop-icon">
                    <i className="bx bx-cloud-upload"></i>
                  </div>
                  <h5 className="modern-drop-title">Dosyayı buraya sürükleyin</h5>
                  <p className="modern-drop-text">veya dosya seçmek için tıklayın</p>
                  <input
                    type="file"
                    className="modern-file-input"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
                    id="fileInput"
                  />
                  <label htmlFor="fileInput" className="modern-file-label">
                    Dosya Seç
                  </label>
                </div>
                <div className="modern-drop-info">
                  <small className="text-muted">
                    <i className="bx bx-info-circle me-1"></i>
                    PDF, Word, Excel, PowerPoint, Resim, TXT, ZIP, RAR (Maks: 50 MB)
                  </small>
                </div>
              </div>

              {uploadForm.file && (
                <div className="modern-file-preview">
                  <div className="modern-file-card">
                    <div className="modern-file-icon-preview">
                      <i className={`bx ${getFileIcon(uploadForm.file.name)}`}></i>
                    </div>
                    <div className="modern-file-details">
                      <h6 className="modern-file-name">{uploadForm.file.name}</h6>
                      <p className="modern-file-size">{(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button
                      className="modern-file-remove"
                      onClick={() => setUploadForm((prev) => ({ ...prev, file: null }))}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  </div>
                </div>
              )}

              <div className="modern-upload-form">
                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-edit me-2"></i>
                    Dosya Başlığı *
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Dosya başlığını girin..."
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-message-square-detail me-2"></i>
                    Açıklama
                  </label>
                  <textarea
                    className="modern-form-textarea"
                    rows="3"
                    placeholder="Dosya hakkında açıklama..."
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="modern-upload-footer">
              <button className="modern-btn-cancel" onClick={() => setShowUploadModal(false)}>
                <i className="bx bx-x me-1"></i>
                İptal
              </button>
              <button
                className="modern-btn-upload"
                onClick={handleUploadSubmit}
                disabled={uploading || !uploadForm.file || !uploadForm.title.trim()}
              >
                {uploading ? (
                  <>
                    <span className="modern-spinner me-2"></span>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <i className="bx bx-upload me-1"></i>
                    Dosyayı Yükle
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredDocuments.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="modern-empty-state text-center py-5">
              <i className="bx bx-folder-open display-1 text-muted mb-4" style={{ color: '#e9ecef' }}></i>
              <h4 className="fw-bold text-dark mb-3">
                {searchTerm ? 'Arama sonucu bulunamadı' : 'Bu klasör boş'}
              </h4>
              <p className="text-muted mb-4">
                {searchTerm
                  ? 'Aradığınız kriterlere uygun doküman bulunamadı.'
                  : 'Henüz bu klasörde hiç dosya bulunmuyor.'}
              </p>
              <button className="btn btn-modern-primary px-4" onClick={() => setShowUploadModal(true)}>
                <i className="bx bx-upload me-1"></i>
                {searchTerm ? 'Yeni Dosya Yükle' : 'İlk Dosyayı Yükleyin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
